import { useState, useMemo, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getKennelStatusBadgeClass, formatKennelStatus, ADOPTION_LEVEL_BADGE_CLASS, STATUS_TRACK_OPTION_GROUPS, STATUS_TRACK_LABELS } from "@/lib/badge-styles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Dog, Cat, Search, MapPin, Calendar, Stethoscope, X as XIcon, LayoutGrid, List, RotateCcw, HelpCircle, AlertCircle, Info, Flame, TrendingUp, TrendingDown, AlertTriangle, Hand, ExternalLink, CheckCircle2, PawPrint, Filter, ArrowUp, ArrowDown, ArrowUpDown, BarChart2, Star, Tag, FileText, Flag, Heart } from "lucide-react";
import { AnimalAvatar } from "@/components/animals/animal-avatar";
import { AnimalCell } from "@/components/animals/animal-cell";
import { CollapsibleFilterHeader } from "@/components/ui/collapsible-filter-header";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Link, useSearch, useLocation } from "wouter";
import { useAuth } from "@/lib/auth-context";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { PageHeader } from "@/components/page-header";
import { ReportFreshnessText } from "@/components/report-freshness-text";
import { AnimalDetailDrawer } from "@/components/animals/AnimalDetailDrawer";
import { AnimalActionMenu } from "@/components/animal-action-menu";
import { FollowAnimalButton } from "@/components/follow-animal-button";
import { EntityLinkerDialog } from "@/components/tasks/entity-linker-dialog";
import { EvalStatusBadge } from "@/components/animals/EvalStatusBadge";
import { getEvalShortLabel } from "@mspca-beti/shared";
import { useActiveLocations } from "@/hooks/use-active-locations";
import { SearchEmptyState } from "@/components/ui/search-empty-state";
import { useColumnFilters, ColumnFilter } from "@/components/ui/column-filter";
import { usePageTitle } from "@/hooks/use-page-title";
import { MultiSelectFilter, parseMultiParam, serializeMultiParam } from "@/components/ui/multi-select-filter";
import { StatusDaysBadge } from "@/components/animals/StatusDaysBadge";
import { AnimalExportButton } from "@/components/ui/animal-export-button";
import { useZoneLabels } from "@/hooks/use-zone-labels";
import { useFollowedAnimalIds } from "@/hooks/use-followed-animals";

// Gate H4.1: Alert flags for behavior monitoring
interface AnimalAlerts {
  struggling: boolean;
  worsening: boolean;
  streak: boolean;
  spike: boolean;
  highSeverity: boolean;
  flagged: boolean;
  // Gate R4: flag signals
  hasLongNoteFlag?: boolean;
  hasKeywordFlag?: boolean;
  hasHighlightFlag?: boolean;
  overdueForInteraction?: boolean;
}

interface AnimalListItem {
  id: string;
  animalId: string;
  name: string;
  species: "DOG" | "CAT" | "OTHER";
  primaryBreed?: string;
  location: string;
  zoneCode: string;
  kennelStatusNorm?: "AVAILABLE" | "UNAVAIL" | "STRAY_WAIT" | "EVALUATION";
  substatusNorm?: "MEDICAL" | "BEHAVIOR" | "BEH+MED" | "PENDADOPT";
  adoptionLevelNorm?: string;
  lastSeenDate: string;
  intakeDate?: string;
  originOrgNorm?: string;
  hasMedicalNotes: boolean;
  isOutcomed: boolean;
  photoUrl: string | null;
  isOnHold: boolean;
  kennelStatusSince?: string;
  kennelStatusSinceIsApprox?: boolean;
  evaluation: {
    status: string | null;
    label: string | null;
    raw: string | null;
    lastEvaluatedAt: string | null;
  };
  alerts?: AnimalAlerts;
}


// Gate E6.6: View mode type
type ViewMode = "cards" | "list";

// Gate E6.6 + E6.8 + J9: Sort options (refactored to column + direction)
type SortColumn = "lastSeen" | "name" | "status" | "zone" | "adoptionLevel" | "evaluationStatus" | "location" | "medical" | "signals" | "intake";
type SortDir = "asc" | "desc";

// Gate E6.6: Domain ordering for sorting (missing values sort to end with 99)
const STATUS_ORDER: Record<string, number> = {
  AVAILABLE: 1,
  STRAY_WAIT: 2,
  EVALUATION: 3,
  UNAVAIL: 4,
};

const ADOPTION_LEVEL_ORDER: Record<string, number> = {
  L1: 1,
  L2: 2,
  L3: 3,
  L4: 4,
};

const EVAL_STATUS_ORDER: Record<string, number> = {
  needs_review: 1,
  needs_recs: 2,
  complete: 3,
};

// Gate E6.6: Helper to get sort order with explicit missing value handling
function getSortOrder(orderMap: Record<string, number>, value: string | null | undefined, missingValue: number = 99): number {
  if (value === null || value === undefined || value === "") {
    return missingValue;
  }
  return orderMap[value] ?? missingValue;
}

// Gate E6.6: Last Seen tooltip text
const LAST_SEEN_TOOLTIP = "Last Seen is the most recent date this animal appeared in a daily census for their location. Animals missing from their location's latest report are considered outcomed.";

// Gate H4.1: Alert badge indicator component for animals list
function AlertBadgeIndicator({ alerts, "data-testid": testId }: { alerts: AnimalAlerts; "data-testid"?: string }) {
  const activeAlerts: { icon: typeof AlertTriangle; label: string; color: string; containerClass: string }[] = [];
  
  if (alerts.struggling) {
    activeAlerts.push({ icon: AlertTriangle, label: "Struggling Now", color: "text-destructive", containerClass: "border-destructive/50 bg-destructive/10" });
  }
  if (alerts.streak) {
    activeAlerts.push({ icon: Flame, label: "3+ Day Streak", color: "text-orange-500", containerClass: "border-destructive/50 bg-destructive/10" });
  }
  if (alerts.spike) {
    activeAlerts.push({ icon: TrendingUp, label: "Rate Spike", color: "text-amber-500", containerClass: "border-destructive/50 bg-destructive/10" });
  }
  if (alerts.worsening) {
    activeAlerts.push({ icon: TrendingDown, label: "Worsening Trend", color: "text-rose-500", containerClass: "border-destructive/50 bg-destructive/10" });
  }
  if (alerts.highSeverity) {
    activeAlerts.push({ icon: AlertCircle, label: "High Severity", color: "text-red-600", containerClass: "border-destructive/50 bg-destructive/10" });
  }
  // Gate R4: flag signal entries
  if (alerts.hasKeywordFlag) {
    activeAlerts.push({ icon: Tag, label: "Keyword Flag", color: "text-info", containerClass: "border-info/50 bg-info/10" });
  }
  if (alerts.hasLongNoteFlag) {
    activeAlerts.push({ icon: FileText, label: "Long Note Flag", color: "text-info", containerClass: "border-info/50 bg-info/10" });
  }
  if (alerts.hasHighlightFlag) {
    activeAlerts.push({ icon: Flag, label: "Highlighted", color: "text-highlight", containerClass: "border-highlight/50 bg-highlight/10" });
  }

  if (activeAlerts.length === 0) return null;

  const tooltipText = activeAlerts.map(a => a.label).join(", ");
  const PrimaryIcon = activeAlerts[0].icon;
  const primaryColor = activeAlerts[0].color;
  const primaryContainerClass = activeAlerts[0].containerClass;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge 
          variant="outline" 
          className={`gap-1 ${primaryContainerClass} ${primaryColor}`}
          data-testid={testId}
        >
          <PrimaryIcon className="h-3 w-3" />
          {activeAlerts.length > 1 && (
            <span className="text-xs">+{activeAlerts.length - 1}</span>
          )}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        <p className="text-sm">{tooltipText}</p>
      </TooltipContent>
    </Tooltip>
  );
}

// Gate E6.6: Props include showOutcomedBadge to control badge visibility per tab
function AnimalCard({ animal, showOutcomedBadge, onViewPreview, onAddTo }: { animal: AnimalListItem; showOutcomedBadge: boolean; onViewPreview?: (animalId: string) => void; onAddTo?: (animalId: string, animalName?: string, animalSpecies?: string, animalPhotoUrl?: string | null) => void }) {
  const { resolveLabel } = useZoneLabels();
  const statusColors = {
    AVAILABLE: "border-l-[hsl(var(--status-available))]",
    UNAVAIL: "border-l-[hsl(var(--status-unavailable))]",
    STRAY_WAIT: "border-l-[hsl(var(--status-stray-wait))]",
    EVALUATION: "border-l-[hsl(var(--status-evaluation))]",
  };

  const [, setLocation] = useLocation();

  return (
    <div data-testid={`card-animal-${animal.id}`}>
      <Card
        className={`hover-elevate cursor-pointer border-l-4 ${animal.isOutcomed ? "border-l-muted-foreground" : (statusColors[animal.kennelStatusNorm as keyof typeof statusColors] || "border-l-muted")}`}
        onClick={() => setLocation(`/animals/${animal.animalId}`)}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-1">
            <div className="flex items-center gap-3">
              <AnimalAvatar animalId={animal.animalId} photoUrl={animal.photoUrl ?? null} name={animal.name} species={animal.species} className="h-20 w-20" />
              <div>
                <CardTitle className="text-lg">{animal.name}</CardTitle>
                <p className="text-sm text-muted-foreground font-mono">{animal.animalId}</p>
              </div>
            </div>
            <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
              <FollowAnimalButton
                animalId={animal.animalId}
                animalName={animal.name}
              />
              <AnimalActionMenu
                animalId={animal.animalId}
                animalName={animal.name}
                animalSpecies={animal.species}
                animalPhotoUrl={animal.photoUrl}
                onViewPreview={onViewPreview}
                onAddTo={onAddTo}
              />
            </div>
          </div>
        </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline">{animal.species}</Badge>
          <StatusDaysBadge
            status={animal.kennelStatusNorm}
            substatus={animal.substatusNorm}
            statusSince={animal.kennelStatusSince}
            isApprox={animal.kennelStatusSinceIsApprox}
            isOutcomed={animal.isOutcomed}
            data-testid={`badge-kennel-status-${animal.animalId}`}
          />
          {/* Gate E6.6 / S20: evaluation badge */}
          <EvalStatusBadge
            status={animal.evaluation.status}
            data-testid={`badge-evaluation-${animal.id}`}
          />
          {animal.adoptionLevelNorm && (
            <Badge className={ADOPTION_LEVEL_BADGE_CLASS}>
              {animal.adoptionLevelNorm}
            </Badge>
          )}
          {showOutcomedBadge && animal.isOutcomed && (
            <Badge 
              variant="secondary" 
              data-testid={`badge-outcomed-card-${animal.id}`}
              title="This animal's stay is over and they no longer appear in later All Pets reports."
            >
              Outcomed
            </Badge>
          )}
          {/* Gate I3: HOLD status badge */}
          {animal.isOnHold && (
            <Badge 
              className="gap-1 bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-700"
              data-testid={`badge-hold-card-${animal.id}`}
              title="This animal is currently on HOLD and not available for adoption."
            >
              <Hand className="h-3 w-3" />
              HOLD
            </Badge>
          )}
          {animal.hasMedicalNotes && (
            <Badge variant="outline" className="gap-1">
              <Stethoscope className="h-3 w-3" />
              Medical Notes
            </Badge>
          )}
          {/* Gate H4.1: Behavior alert indicators */}
          {animal.alerts?.flagged && (
            <AlertBadgeIndicator alerts={animal.alerts} data-testid={`alert-indicator-${animal.id}`} />
          )}
        </div>
        
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <MapPin className="h-3 w-3" />
            <span>{animal.location} - {resolveLabel(animal.zoneCode)}</span>
          </div>
          {animal.primaryBreed && (
            <div className="text-muted-foreground">
              {animal.primaryBreed}
            </div>
          )}
          {/* Gate E6.8: Intake date display */}
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-3 w-3" />
            <span>Intake: {animal.intakeDate ? format(new Date(animal.intakeDate), "MMM d, yyyy") : "—"}</span>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2 text-muted-foreground cursor-help">
                <Calendar className="h-3 w-3" />
                <span>Last seen: {format(new Date(animal.lastSeenDate), "MMM d, yyyy")}</span>
                <HelpCircle className="h-3 w-3 opacity-50" />
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p>{LAST_SEEN_TOOLTIP}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </CardContent>
      </Card>
    </div>
  );
}

interface AnimalListViewProps {
  animals: AnimalListItem[];
  showOutcomedBadge: boolean;
  showLocation: boolean;
  onAnimalClick?: (animalId: string) => void;
  onViewPreview?: (animalId: string) => void;
  onAddTo?: (animalId: string, animalName?: string, animalSpecies?: string, animalPhotoUrl?: string | null) => void;
  columnFilterValues: Record<string, string[]>;
  columnFilterSelections: Record<string, Set<string>>;
  columnFilterIsActive: (column: string) => boolean;
  onColumnFilterChange: (column: string, values: Set<string>) => void;
  onColumnFilterClear: (column: string) => void;
  hasAnyColumnFilter: boolean;
  onClearAllColumnFilters: () => void;
  totalBeforeColumnFilter: number;
  sortColumn: SortColumn;
  sortDir: SortDir;
  onColumnSort: (column: SortColumn) => void;
}

function AnimalListView({ animals, showOutcomedBadge, showLocation, onAnimalClick, onViewPreview, onAddTo, columnFilterValues, columnFilterSelections, columnFilterIsActive, onColumnFilterChange, onColumnFilterClear, hasAnyColumnFilter, onClearAllColumnFilters, totalBeforeColumnFilter, sortColumn, sortDir, onColumnSort }: AnimalListViewProps) {
  const { resolveLabel } = useZoneLabels();
  // Gate S19 amendment: follow wiring — hoisted to component top level, not inside .map()
  const { data: followsData } = useFollowedAnimalIds();
  const followedIds = followsData?.animalIds ?? [];
  const stickyTh = "sticky top-0 z-30 bg-card shadow-[0_1px_0_0_hsl(var(--border))]";
  const renderSortIcon = (column: SortColumn) => (
    sortColumn === column ? (
      sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
    ) : (
      <ArrowUpDown className="h-3 w-3 opacity-50" />
    )
  );

  const renderSortButton = (column: SortColumn, label: string) => (
    <button
      className="flex items-center gap-1 hover:text-foreground transition-colors cursor-pointer"
      onClick={() => onColumnSort(column)}
      data-testid={`sort-${column}`}
    >
      {label}
      {renderSortIcon(column)}
    </button>
  );

  const TALL_FILTER_COLUMNS = new Set(["name", "intake", "lastSeen"]);

  const renderFilter = (columnKey: string) => (
    <ColumnFilter
      columnKey={columnKey}
      values={columnFilterValues[columnKey] || []}
      selectedValues={columnFilterSelections[columnKey]}
      onSelectionChange={onColumnFilterChange}
      onClear={onColumnFilterClear}
      isActive={columnFilterIsActive(columnKey)}
      maxHeight={TALL_FILTER_COLUMNS.has(columnKey) ? "20rem" : undefined}
    />
  );

  return (
    <div className="rounded-md border border-border bg-card w-full max-w-full overflow-auto" style={{ maxHeight: "calc(100vh - 14rem)" }}>
      <table className="w-full caption-bottom text-sm min-w-[850px] table-zebra">
        <TableHeader>
          <TableRow>
            <TableHead className="sticky top-0 z-30 bg-card shadow-[0_1px_0_0_hsl(var(--border))] w-[120px] max-w-[120px]">
              <div className="flex items-center gap-1">
                {renderSortButton("name", "Animal")}
                {renderFilter("name")}
              </div>
            </TableHead>
            {showLocation && (
              <TableHead className={stickyTh}>
                <div className="flex items-center gap-1">
                  {renderSortButton("location", "Location")}
                  {renderFilter("location")}
                </div>
              </TableHead>
            )}
            <TableHead className={stickyTh}>
              <div className="flex items-center gap-1">
                {renderSortButton("zone", "Kennel")}
                {renderFilter("kennel")}
              </div>
            </TableHead>
            <TableHead className={stickyTh}>
              <div className="flex items-center gap-1">
                {renderSortButton("status", "Status")}
                {renderFilter("status")}
              </div>
            </TableHead>
            <TableHead className={`${stickyTh} whitespace-nowrap`}>
              <div className="flex items-center gap-1">
                {renderSortButton("adoptionLevel", "Adopt Lvl")}
                {renderFilter("adoptionLevel")}
              </div>
            </TableHead>
            <TableHead className={stickyTh}>
              <div className="flex items-center gap-1">
                {renderSortButton("evaluationStatus", "Evaluation")}
                {renderFilter("evaluation")}
              </div>
            </TableHead>
            <TableHead className={stickyTh}>
              <div className="flex items-center gap-1">
                {renderSortButton("medical", "Medical")}
                {renderFilter("medical")}
              </div>
            </TableHead>
            <TableHead className={stickyTh}>
              <div className="flex items-center gap-1">
                {renderSortButton("signals", "Signals")}
                {renderFilter("signals")}
              </div>
            </TableHead>
            <TableHead className={stickyTh}>
              <div className="flex items-center gap-1">
                {renderSortButton("intake", "Intake Date")}
                {renderFilter("intake")}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-3 w-3 opacity-50 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>Intake date of the animal's current stay at the shelter. Animals that leave and return will show the start of their most recent stay.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </TableHead>
            <TableHead className={stickyTh}>
              <div className="flex items-center gap-1">
                {renderSortButton("lastSeen", "Last Seen")}
                {renderFilter("lastSeen")}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-3 w-3 opacity-50 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>{LAST_SEEN_TOOLTIP}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </TableHead>
            <TableHead className={`w-12 ${stickyTh} text-right`}>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {animals.length === 0 && hasAnyColumnFilter && totalBeforeColumnFilter > 0 ? (
            <TableRow>
              <TableCell colSpan={showLocation ? 11 : 10} className="h-24 text-center">
                <div className="flex flex-col items-center gap-2">
                  <Filter className="h-8 w-8 text-muted-foreground/50" />
                  <p className="text-sm text-muted-foreground">No animals match the active column filters</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onClearAllColumnFilters}
                    data-testid="button-clear-column-filters"
                  >
                    Clear column filters
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ) : animals.length === 0 ? (
            <TableRow>
              <TableCell colSpan={showLocation ? 11 : 10} className="h-24 text-center">
                <SearchEmptyState />
              </TableCell>
            </TableRow>
          ) : animals.map((animal) => (
            <TableRow 
              key={animal.id} 
              className="transition-colors hover:bg-muted/50 cursor-pointer"
              data-testid={`row-animal-${animal.id}`}
              onClick={() => onAnimalClick?.(animal.animalId)}
            >
              <TableCell className="font-medium">
                <AnimalCell
                  animalId={animal.animalId}
                  photoUrl={animal.photoUrl ?? null}
                  name={animal.name}
                  species={animal.species}
                  inlineBadges={
                    <>
                      {/* Gate S19: Heart and Flag stay with species badge — never wrap */}
                      {followedIds.includes(animal.animalId) && (
                        <Heart className="h-3 w-3 fill-rose-500 text-rose-500 shrink-0" data-testid={`heart-followed-${animal.animalId}`} />
                      )}
                      {animal.alerts?.hasHighlightFlag && (
                        <Flag className="h-3 w-3 fill-amber-500 text-amber-500 shrink-0" data-testid={`flag-highlighted-list-${animal.animalId}`} />
                      )}
                    </>
                  }
                  wrapBadges={
                    <>
                      {/* Gate S19: Wide text badges — allowed to wrap below the nowrap group */}
                      {showOutcomedBadge && animal.isOutcomed && (
                        <Badge
                          variant="secondary"
                          className="text-xs"
                          data-testid={`badge-outcomed-list-${animal.id}`}
                        >
                          Outcomed
                        </Badge>
                      )}
                      {animal.isOnHold && (
                        <Badge
                          className="gap-1 text-xs bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-700"
                          data-testid={`badge-hold-list-${animal.id}`}
                          title="This animal is currently on HOLD"
                        >
                          <Hand className="h-3 w-3" />
                          HOLD
                        </Badge>
                      )}
                    </>
                  }
                />
              </TableCell>
              {showLocation && <TableCell>{animal.location}</TableCell>}
              <TableCell className="text-sm text-muted-foreground">{resolveLabel(animal.zoneCode)}</TableCell>
              <TableCell>
                <StatusDaysBadge
                  status={animal.kennelStatusNorm}
                  substatus={animal.substatusNorm}
                  statusSince={animal.kennelStatusSince}
                  isApprox={animal.kennelStatusSinceIsApprox}
                  isOutcomed={animal.isOutcomed}
                  data-testid={`badge-kennel-status-${animal.animalId}`}
                />
              </TableCell>
              <TableCell>
                {animal.adoptionLevelNorm && (
                  <Badge className={ADOPTION_LEVEL_BADGE_CLASS}>
                    {animal.adoptionLevelNorm}
                  </Badge>
                )}
              </TableCell>
              <TableCell>
                <EvalStatusBadge
                  status={animal.evaluation.status}
                  data-testid={`badge-evaluation-list-${animal.id}`}
                />
              </TableCell>
              <TableCell>
                {animal.hasMedicalNotes && (
                  <Badge variant="outline" className="gap-1">
                    <Stethoscope className="h-3 w-3" />
                    Med
                  </Badge>
                )}
              </TableCell>
              {/* Gate H4.1: Alerts cell */}
              <TableCell>
                {animal.alerts?.flagged && (
                  <AlertBadgeIndicator alerts={animal.alerts} data-testid={`alert-indicator-list-${animal.id}`} />
                )}
              </TableCell>
              {/* Gate E6.8: Intake Date cell */}
              <TableCell className="text-sm text-muted-foreground">
                {animal.intakeDate ? format(new Date(animal.intakeDate), "MMM d, yyyy") : "—"}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {format(new Date(animal.lastSeenDate), "MMM d, yyyy")}
              </TableCell>
              <TableCell onClick={(e) => e.stopPropagation()}>
                <AnimalActionMenu
                  animalId={animal.animalId}
                  animalName={animal.name}
                  animalSpecies={animal.species}
                  animalPhotoUrl={animal.photoUrl}
                  onViewPreview={onViewPreview}
                  onAddTo={onAddTo}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </table>
    </div>
  );
}

// Gate E6.2: Type for active tab
type ActiveTab = "current" | "history";

// Gate E6.5: Simplified outcome filter (removed "current" option)
type OutcomeFilter = "all" | "outcomed";
// Gate I3.1: HOLD filter type
type HoldFilter = "all" | "onHold" | "notOnHold";

// Gate F18: Freshness data interface
interface FreshnessData {
  serverNowUtc: string;
  expectedDateLocal: string;
  minExpectedReportLocalDate?: string; // F18.1
  locations: Array<{
    locationCode: string;
    locationLabel: string;
    lastUpdatedAtUtc: string | null;
    lastReportLocalDate: string | null;
    isCurrent: boolean;
    staleDays: number;
    missingDatesLocal?: string[]; // F18.1
    missingCountTotal?: number; // F18.1
    missingCountReturned?: number; // F18.1
    hasNoBaseline?: boolean; // F18.1: True if never had a report
  }>;
  summary: {
    anyStale: boolean;
    staleCount: number;
    currentCount: number;
  };
}

interface MetricRecord {
  id: string;
  label: string;
  description: string | null;
}

export default function Animals() {
  usePageTitle("Animals");
  const { resolveLabel } = useZoneLabels();
  // Gate F13: Read initial location from URL search params
  // Gate I3.1: Also read holdFilter from URL (handles legacy boolean and symbolic values)
  // Zone filter: Read from URL (only valid when location is set)
  const searchString = useSearch();
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const urlParams = new URLSearchParams(searchString);

  // Metric Builder: detect metricId in URL for metric mode
  const metricId = urlParams.get("metricId") || null;
  const metricLocationOverride = metricId ? (urlParams.get("location") || null) : null;
  const initialLocation = urlParams.get("location") || "all";
  const initialZone = urlParams.get("zone") || "all";
  const onHoldParam = urlParams.get("onHold");
  const initialHoldFilter: HoldFilter = 
    onHoldParam === "onHold" ? "onHold" :
    onHoldParam === "notOnHold" ? "notOnHold" :
    onHoldParam === "true" ? "onHold" :  // Legacy boolean support
    onHoldParam === "false" ? "notOnHold" :  // Legacy boolean support
    "all";
  
  // Gate E6.2: Add tab state (default to "current")
  const [activeTab, setActiveTab] = useState<ActiveTab>(
    urlParams.get("tab") === "history" ? "history" : "current"
  );
  
  // Gate E6.6: View mode state
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem("animals-view-mode");
    return saved === "list" || saved === "cards" ? saved : "list";
  });
  
  // Gate E6.6 + J9: Sort state (column + direction)
  const [sortColumn, setSortColumn] = useState<SortColumn>("lastSeen");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  
  const [filterCollapsed, setFilterCollapsed] = useState<boolean>(() => window.innerWidth < 640);
  const [searchQuery, setSearchQuery] = useState(urlParams.get("search") || "");
  const hasUrlLocation = useRef(parseMultiParam(initialLocation).length > 0);
  const defaultLocationSeeded = useRef(false);
  const [selectedLocations, setSelectedLocations] = useState<string[]>(parseMultiParam(initialLocation));
  const [selectedZone, setSelectedZone] = useState<string>(initialLocation !== "all" && !initialLocation.includes(",") ? initialZone : "all");
  const [selectedSpeciesList, setSelectedSpeciesList] = useState<string[]>(parseMultiParam(urlParams.get("species")));
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(parseMultiParam(urlParams.get("status")));
  const [selectedAdoptionLevels, setSelectedAdoptionLevels] = useState<string[]>(parseMultiParam(urlParams.get("adoptionLevelNorm")));
  const [showMedicalNotesOnly, setShowMedicalNotesOnly] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerAnimalId, setDrawerAnimalId] = useState<string | null>(null);

  const [linkerOpen, setLinkerOpen] = useState(false);
  const [linkerAnimalId, setLinkerAnimalId] = useState<string | null>(null);
  const [linkerAnimalName, setLinkerAnimalName] = useState<string | undefined>(undefined);
  const [linkerAnimalSpecies, setLinkerAnimalSpecies] = useState<string | undefined>(undefined);
  const [linkerAnimalPhotoUrl, setLinkerAnimalPhotoUrl] = useState<string | null | undefined>(undefined);

  const handleAnimalClick = (animalId: string) => {
    setDrawerAnimalId(animalId);
    setDrawerOpen(true);
  };

  const handleViewPreview = (animalId: string) => {
    setDrawerAnimalId(animalId);
    setDrawerOpen(true);
  };

  const handleAddTo = (animalId: string, animalName?: string, animalSpecies?: string, animalPhotoUrl?: string | null) => {
    setLinkerAnimalId(animalId);
    setLinkerAnimalName(animalName);
    setLinkerAnimalSpecies(animalSpecies);
    setLinkerAnimalPhotoUrl(animalPhotoUrl);
    setLinkerOpen(true);
  };
  
  // Gate E6.4: New filter states
  const [outcomeFilter, setOutcomeFilter] = useState<OutcomeFilter>(
    urlParams.get("outcomeFilter") === "outcomed" ? "outcomed" : "all"
  );
  const [selectedEvalStatuses, setSelectedEvalStatuses] = useState<string[]>(parseMultiParam(urlParams.get("evalStatus")));
  // Gate I3.1: HOLD filter state with URL persistence
  const [holdFilter, setHoldFilter] = useState<HoldFilter>(initialHoldFilter);
  
  // Phase 1c: Seed default location once user resolves (if no URL location was present at mount)
  useEffect(() => {
    if (!defaultLocationSeeded.current && !hasUrlLocation.current && user?.defaultLocationCode) {
      setSelectedLocations([user.defaultLocationCode]);
      defaultLocationSeeded.current = true;
    }
  }, [user?.defaultLocationCode]);

  // Gate I3.1: Sync holdFilter to URL query params
  useEffect(() => {
    const url = new URL(window.location.href);
    if (holdFilter !== "all") {
      url.searchParams.set("onHold", holdFilter);
    } else {
      url.searchParams.delete("onHold");
    }
    window.history.replaceState({}, "", url.toString());
  }, [holdFilter]);
  
  useEffect(() => {
    const params = new URLSearchParams(searchString);
    const urlLocations = parseMultiParam(params.get("location"));
    const urlZone = params.get("zone") || "all";
    const locKey = urlLocations.join(",");
    const curKey = selectedLocations.join(",");
    if (locKey !== curKey) {
      setSelectedLocations(urlLocations);
      setSelectedZone(urlLocations.length === 1 ? urlZone : "all");
    }
  }, [searchString]);

  // Gate F18.1: Dismiss state for freshness alert (session-only)
  
  // Gate F6.6: Dynamic locations from API
  const { locations: activeLocations, isLoading: isLocationsLoading, error: locationsError } = useActiveLocations();

  // Zone filter: Fetch zones for the selected location
  interface ZoneData {
    id: string;
    zoneCode: string;
    areaLabel: string;
    location: string;
  }
  const { data: zonesData } = useQuery<{ zones: ZoneData[] }>({
    queryKey: ["/api/zones"],
  });

  // Filter zones for the selected location (only when exactly one location selected)
  const zonesForLocation = useMemo(() => {
    if (!zonesData?.zones || selectedLocations.length !== 1) return [];
    return zonesData.zones.filter(z => z.location === selectedLocations[0]);
  }, [zonesData, selectedLocations]);

  // Reset zone when locations change (but not on initial mount to preserve URL params)
  const prevLocationRef = useRef(selectedLocations.join(","));
  useEffect(() => {
    const cur = selectedLocations.join(",");
    if (prevLocationRef.current !== cur) {
      setSelectedZone("all");
      prevLocationRef.current = cur;
    }
  }, [selectedLocations]);

  // Sync all multi-select filters + zone to URL query params
  useEffect(() => {
    const url = new URL(window.location.href);

    const locSer = serializeMultiParam(selectedLocations);
    if (locSer) url.searchParams.set("location", locSer);
    else url.searchParams.delete("location");

    if (selectedLocations.length === 1 && selectedZone !== "all") {
      url.searchParams.set("zone", selectedZone);
    } else {
      url.searchParams.delete("zone");
    }

    const specSer = serializeMultiParam(selectedSpeciesList);
    if (specSer) url.searchParams.set("species", specSer);
    else url.searchParams.delete("species");

    const statSer = serializeMultiParam(selectedStatuses);
    if (statSer) url.searchParams.set("status", statSer);
    else url.searchParams.delete("status");

    const advSer = serializeMultiParam(selectedAdoptionLevels);
    if (advSer) url.searchParams.set("adoptionLevelNorm", advSer);
    else url.searchParams.delete("adoptionLevelNorm");

    const evalSer = serializeMultiParam(selectedEvalStatuses);
    if (evalSer) url.searchParams.set("evalStatus", evalSer);
    else url.searchParams.delete("evalStatus");

    window.history.replaceState({}, "", url.toString());
  }, [selectedLocations, selectedZone, selectedSpeciesList, selectedStatuses, selectedAdoptionLevels, selectedEvalStatuses]);

  useEffect(() => {
    localStorage.setItem("animals-view-mode", viewMode);
  }, [viewMode]);

  // Gate E6.6: Centralized reset helper
  const resetToDefaults = () => {
    setActiveTab("current");
    setViewMode("cards");
    setSortColumn("lastSeen");
    setSortDir("desc");
    setSearchQuery("");
    setSelectedLocations([]);
    setSelectedZone("all");
    setSelectedSpeciesList([]);
    setSelectedStatuses([]);
    setSelectedAdoptionLevels([]);
    setShowMedicalNotesOnly(false);
    setOutcomeFilter("all");
    setSelectedEvalStatuses([]);
    setHoldFilter("all"); // Gate I3.1
  };

  // Gate E6.2 + E6.4: Reset filters when switching tabs (includes new filters)
  const handleTabChange = (tab: string) => {
    setActiveTab(tab as ActiveTab);
    setSearchQuery("");
    setSelectedLocations([]);
    setSelectedZone("all");
    setSelectedSpeciesList([]);
    setSelectedStatuses([]);
    setSelectedAdoptionLevels([]);
    setShowMedicalNotesOnly(false);
    setOutcomeFilter("all");
    setSelectedEvalStatuses([]);
    setHoldFilter("all"); // Gate I3.1
  };

  const handleColumnSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDir(column === "lastSeen" || column === "intake" ? "desc" : "asc");
    }
  };

  // Gate E6.2 + E6.4 + I3.1: Check if any filters are active
  const hasActiveFilters = searchQuery !== "" ||
    selectedLocations.length > 0 ||
    selectedZone !== "all" ||
    selectedSpeciesList.length > 0 ||
    selectedStatuses.length > 0 ||
    selectedAdoptionLevels.length > 0 ||
    showMedicalNotesOnly ||
    outcomeFilter !== "all" ||
    selectedEvalStatuses.length > 0 ||
    holdFilter !== "all"; // Gate I3.1

  const isDefaultLocationActive =
    selectedLocations.length === 1 &&
    selectedLocations[0] === user?.defaultLocationCode;

  // Gate E6.6: Check if view is not default (for Reset View button visibility)
  const isNotDefaultView = activeTab !== "current" ||
    viewMode !== "cards" ||
    sortColumn !== "lastSeen" ||
    sortDir !== "desc" ||
    hasActiveFilters;

  // Metric Builder: fetch metric label when metricId present
  const { data: metricRecord } = useQuery<MetricRecord>({
    queryKey: ["/api/metrics", metricId],
    queryFn: async () => {
      const res = await fetch(`/api/metrics/${metricId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Metric not found");
      return res.json();
    },
    enabled: !!metricId,
    staleTime: 5 * 60 * 1000,
  });

  // Metric Builder: clear metric mode by removing metricId from URL
  const clearMetricMode = () => {
    const newParams = new URLSearchParams(searchString);
    newParams.delete("metricId");
    newParams.delete("location");
    setLocation(`/animals${newParams.toString() ? `?${newParams.toString()}` : ""}`);
  };

  // Build query string for API call
  let apiUrl: string;
  let queryKey: unknown[];
  if (metricId) {
    const metricParams = new URLSearchParams({ metricId });
    if (metricLocationOverride) metricParams.set("location", metricLocationOverride);
    apiUrl = `/api/animals?${metricParams.toString()}`;
    queryKey = ["/api/animals", "metric", metricId, metricLocationOverride ?? "all"];
  } else {
    const params = new URLSearchParams();
    const locSer = serializeMultiParam(selectedLocations); if (locSer) params.set("location", locSer);
    if (selectedLocations.length === 1 && selectedZone !== "all") params.set("zone", selectedZone);
    const specSer = serializeMultiParam(selectedSpeciesList); if (specSer) params.set("species", specSer);
    const statSer = serializeMultiParam(selectedStatuses); if (statSer) params.set("statusTrack", statSer);
    const advSer = serializeMultiParam(selectedAdoptionLevels); if (advSer) params.set("adoptionLevelNorm", advSer);
    if (showMedicalNotesOnly) params.set("hasMedicalNotes", "true");
    if (activeTab === "current") params.set("currentOnly", "true");
    if (holdFilter === "onHold") params.set("onHold", "true");
    if (holdFilter === "notOnHold") params.set("onHold", "false");
    const queryString = params.toString();
    apiUrl = queryString ? `/api/animals?${queryString}` : "/api/animals";
    queryKey = ["/api/animals", activeTab, selectedLocations.join(","), selectedZone, selectedSpeciesList.join(","), selectedStatuses.join(","), selectedAdoptionLevels.join(","), showMedicalNotesOnly, holdFilter];
  }

  const { data: animalsResponse, isLoading, isFetching } = useQuery<{ animals: AnimalListItem[]; total: number }>({
    queryKey,
    queryFn: async () => {
      const response = await fetch(apiUrl, {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch animals: ${response.statusText}`);
      }
      return response.json();
    },
    placeholderData: (previousData) => previousData,
  });

  // Gate F18: Fetch freshness data for stale report alerts
  const { data: freshness } = useQuery<FreshnessData>({
    queryKey: ["/api/dashboard/freshness"],
  });

  const data = animalsResponse?.animals || [];

  // Gate F18: Build stale locations list for alert
  const staleLocations = useMemo(() => {
    if (!freshness?.locations) return [];
    return freshness.locations.filter(loc => !loc.isCurrent);
  }, [freshness]);

  // Gate F18: Determine which stale locations to show in alert based on filter
  const staleMsgLocations = useMemo(() => {
    if (selectedLocations.length > 0) {
      // Show alert only for selected locations that are stale
      return staleLocations.filter(loc => selectedLocations.includes(loc.locationCode));
    }
    return staleLocations;
  }, [selectedLocations, staleLocations]);

  const filteredAnimals = useMemo(() => {
    let result = data.filter((animal) => {
      // In metric mode, server already applied exact conditions — bypass all client-side filters
      if (metricId) return true;

      const matchesSearch = !searchQuery || 
        animal.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        animal.animalId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (animal.primaryBreed && animal.primaryBreed.toLowerCase().includes(searchQuery.toLowerCase()));
      
      // Gate E6.5: Outcome filter (History tab only, simplified)
      let matchesOutcome = true;
      if (activeTab === "history" && outcomeFilter === "outcomed") {
        matchesOutcome = animal.isOutcomed === true;
      }
      
      // Gate E6.4: Evaluation filter (both tabs)
      let matchesEvaluation = true;
      if (selectedEvalStatuses.length > 0) {
        const status = animal.evaluation?.status;
        matchesEvaluation = selectedEvalStatuses.some(sel => {
          if (sel === "none") return status === null || status === undefined;
          if (sel === "other") return status !== null && status !== undefined &&
            !["needs_review", "needs_recs", "complete"].includes(status);
          return status === sel;
        });
      }
      
      return matchesSearch && matchesOutcome && matchesEvaluation;
    });

    // Gate E6.6 + E6.8 + J9: Apply sorting (column + direction)
    const dir = sortDir === "asc" ? 1 : -1;
    result = [...result].sort((a, b) => {
      let cmp = 0;
      switch (sortColumn) {
        case "lastSeen":
          cmp = new Date(a.lastSeenDate).getTime() - new Date(b.lastSeenDate).getTime();
          break;
        case "name":
          cmp = a.name.localeCompare(b.name);
          break;
        case "status": {
          const statusA = getSortOrder(STATUS_ORDER, a.kennelStatusNorm);
          const statusB = getSortOrder(STATUS_ORDER, b.kennelStatusNorm);
          cmp = statusA - statusB;
          break;
        }
        case "zone":
          cmp = (a.zoneCode || "").localeCompare(b.zoneCode || "");
          break;
        case "adoptionLevel": {
          const levelA = getSortOrder(ADOPTION_LEVEL_ORDER, a.adoptionLevelNorm);
          const levelB = getSortOrder(ADOPTION_LEVEL_ORDER, b.adoptionLevelNorm);
          cmp = levelA - levelB;
          break;
        }
        case "evaluationStatus": {
          const evalA = getSortOrder(EVAL_STATUS_ORDER, a.evaluation?.status);
          const evalB = getSortOrder(EVAL_STATUS_ORDER, b.evaluation?.status);
          cmp = evalA - evalB;
          break;
        }
        case "location":
          cmp = a.location.localeCompare(b.location);
          break;
        case "medical": {
          const medA = a.hasMedicalNotes ? 0 : 1;
          const medB = b.hasMedicalNotes ? 0 : 1;
          cmp = medA - medB;
          break;
        }
        case "signals": {
          const alertA = a.alerts?.flagged ? 0 : 1;
          const alertB = b.alerts?.flagged ? 0 : 1;
          cmp = alertA - alertB;
          break;
        }
        case "intake": {
          if (!a.intakeDate && !b.intakeDate) { cmp = 0; break; }
          if (!a.intakeDate) return 1;
          if (!b.intakeDate) return -1;
          cmp = new Date(a.intakeDate).getTime() - new Date(b.intakeDate).getTime();
          break;
        }
        default:
          cmp = 0;
      }
      return cmp * dir;
    });

    return result;
  }, [data, searchQuery, activeTab, outcomeFilter, selectedEvalStatuses, sortColumn, sortDir]);

  const showLocation = selectedLocations.length === 0;

  const columnFilterResetKey = `${activeTab}|${selectedLocations.join(",")}|${selectedZone}|${selectedSpeciesList.join(",")}|${selectedStatuses.join(",")}|${selectedAdoptionLevels.join(",")}|${showMedicalNotesOnly}|${holdFilter}|${outcomeFilter}|${selectedEvalStatuses.join(",")}`;
  const columnFilters = useColumnFilters(columnFilterResetKey);

  useEffect(() => {
    if (viewMode === "cards") {
      columnFilters.clearAll();
    }
  }, [viewMode]);

  const formatDateFilter = (dateStr?: string | null) => {
    if (!dateStr) return "";
    try { return format(new Date(dateStr), "MMM d, yyyy"); } catch { return ""; }
  };

  const columnFilterValues = useMemo(() => {
    const vals: Record<string, string[]> = {
      name: [...new Set(filteredAnimals.map(a => a.name))],
      status: [...new Set(filteredAnimals.map(a => formatKennelStatus(a.kennelStatusNorm, a.substatusNorm)))],
      adoptionLevel: [...new Set(filteredAnimals.map(a => a.adoptionLevelNorm || ""))],
      evaluation: [...new Set(filteredAnimals.map(a => a.evaluation?.status ? getEvalShortLabel(a.evaluation.status) : ""))],
      kennel: [...new Set(filteredAnimals.map(a => a.zoneCode || ""))],
      medical: [...new Set(filteredAnimals.map(a => a.hasMedicalNotes ? "Yes" : "No"))],
      signals: [...new Set(filteredAnimals.map(a => a.alerts?.flagged ? "Flagged" : "None"))],
      intake: [...new Set(filteredAnimals.map(a => formatDateFilter(a.intakeDate)))],
      lastSeen: [...new Set(filteredAnimals.map(a => formatDateFilter(a.lastSeenDate)))],
    };
    if (showLocation) {
      vals.location = [...new Set(filteredAnimals.map(a => a.location))];
    }
    return vals;
  }, [filteredAnimals, showLocation]);

  const columnFilterGetters: Record<string, (a: AnimalListItem) => string> = useMemo(() => ({
    name: (a) => a.name,
    status: (a) => formatKennelStatus(a.kennelStatusNorm, a.substatusNorm),
    location: (a) => a.location,
    adoptionLevel: (a) => a.adoptionLevelNorm || "",
    evaluation: (a) => a.evaluation?.status ? getEvalShortLabel(a.evaluation.status) : "",
    kennel: (a) => a.zoneCode || "",
    medical: (a) => a.hasMedicalNotes ? "Yes" : "No",
    signals: (a) => a.alerts?.flagged ? "Flagged" : "None",
    intake: (a) => formatDateFilter(a.intakeDate),
    lastSeen: (a) => formatDateFilter(a.lastSeenDate),
  }), []);

  const columnFilteredAnimals = useMemo(() => {
    return columnFilters.applyFilters(filteredAnimals, columnFilterGetters);
  }, [filteredAnimals, columnFilters.filters, columnFilterGetters]);

  // Gate E6.5: Build dynamic description line
  const description = useMemo(() => {
    const parts: string[] = [];
    
    // View type prefix with outcome phrase (History tab only)
    if (activeTab === "current") {
      parts.push("Current population of");
    } else {
      // Gate E6.5: Simplified - only "All" or "All outcomed"
      if (outcomeFilter === "outcomed") {
        parts.push("All outcomed");
      } else {
        parts.push("All");
      }
    }
    
    // Species phrase
    const speciesMap: Record<string, string> = { 
      DOG: "dogs", 
      CAT: "cats", 
      OTHER: "other species" 
    };
    if (activeTab === "current") {
      parts.push(selectedSpeciesList.length === 1 ? `all ${speciesMap[selectedSpeciesList[0]]}` : "all animals");
    } else {
      parts.push(selectedSpeciesList.length === 1 ? speciesMap[selectedSpeciesList[0]] : "animals");
      parts.push("(history)");
    }
    
    // Status phrase
    if (selectedStatuses.length > 0) {
      if (selectedStatuses.length === 1) {
        const label = STATUS_TRACK_LABELS[selectedStatuses[0]];
        if (label) parts.push(`with status: ${label}`);
      } else {
        parts.push(`with ${selectedStatuses.length} statuses`);
      }
    }
    
    // Gate E6.4: Evaluation phrase (both tabs)
    if (selectedEvalStatuses.length > 0) {
      const evalMap: Record<string, string> = {
        needs_review: "needing review",
        needs_recs: "needing recommendations",
        complete: "with complete evaluations",
        none: "without evaluations",
        other: "with other evaluation status"
      };
      if (selectedEvalStatuses.length === 1) parts.push(evalMap[selectedEvalStatuses[0]] || "");
      else parts.push(`with ${selectedEvalStatuses.length} evaluation statuses`);
    }
    
    // Adoption level phrase
    if (selectedAdoptionLevels.length === 1) {
      parts.push(selectedAdoptionLevels[0] === "none" ? "with no adoption level" : `at adoption level ${selectedAdoptionLevels[0]}`);
    } else if (selectedAdoptionLevels.length > 1) {
      parts.push(`at ${selectedAdoptionLevels.length} adoption levels`);
    }
    
    // Medical notes phrase
    if (showMedicalNotesOnly) {
      parts.push("with medical notes");
    }
    
    // Location phrase
    const locationMap: Record<string, string> = {
      SALEM: "at Salem",
      BOSTON: "at Boston", 
      METHUEN: "at Methuen",
      CAPE: "at Cape Cod"
    };
    if (selectedLocations.length === 1) {
      parts.push(locationMap[selectedLocations[0]] ?? `at ${selectedLocations[0]}`);
    } else if (selectedLocations.length > 1) {
      parts.push(`at ${selectedLocations.length} locations`);
    } else {
      parts.push("across all locations");
    }
    
    // Zone/kennel phrase (only when zone is selected and exactly one location)
    if (selectedZone !== "all" && selectedLocations.length === 1) {
      parts.push(`in ${selectedZone}`);
    }
    
    const text = parts.join(" ") + ".";
    if (isDefaultLocationActive) {
      return (
        <>
          <Star className="inline-block h-3 w-3 text-amber-400 fill-amber-400 mr-0.5" />
          {" "}{text}
        </>
      );
    }
    return text;
  }, [activeTab, selectedSpeciesList, selectedStatuses, selectedAdoptionLevels, showMedicalNotesOnly, selectedLocations, selectedZone, outcomeFilter, selectedEvalStatuses, isDefaultLocationActive]);

  // Gate E6.2: Count outcomed animals (only relevant in history tab)
  const outcomedCount = useMemo(() => {
    if (activeTab === "current") return 0;
    return filteredAnimals.filter(a => a.isOutcomed).length;
  }, [activeTab, filteredAnimals]);

  if (isLoading) {
    return (
      <div className="flex-1 px-4 sm:px-6 pb-6 pt-4">
        <PageHeader
          breadcrumb={[{ label: "Reporting" }, { label: "Animals" }]}
          title={
            <span className="flex items-center gap-2" data-testid="heading-animals">
              <PawPrint className="w-6 h-6 text-primary" />
              Animals
            </span>
          }
        />
        
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-24 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 px-4 sm:px-6 pb-6 pt-4">
      <PageHeader
        breadcrumb={[{ label: "Reporting" }, { label: "Animals" }]}
        title={
          <span className="flex items-center gap-2" data-testid="heading-animals">
            <PawPrint className="w-6 h-6 text-primary" />
            Animals
          </span>
        }
        actions={<ReportFreshnessText reportTypes={["allPets", "interaction"]} testIdPrefix="animals" />}
      />

      {/* Metric Builder: metric mode banner */}
      {metricId && (
        <>
          <div className="mb-3 flex items-center gap-3 rounded-md border bg-muted/50 px-4 py-3" data-testid="banner-metric-mode">
            <BarChart2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-sm text-muted-foreground">Filtered by: </span>
              <span className="text-sm font-medium" data-testid="text-metric-label">
                {metricRecord?.label ?? "Loading…"}
              </span>
              {metricLocationOverride && (
                <>
                  <span className="text-sm text-muted-foreground"> · </span>
                  <span className="text-sm text-muted-foreground">
                    {({ SALEM: "Salem", BOSTON: "Boston", METHUEN: "Methuen", CAPE: "Cape" } as Record<string, string>)[metricLocationOverride] ?? metricLocationOverride}
                  </span>
                </>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearMetricMode}
              data-testid="button-clear-metric-mode"
            >
              <XIcon className="h-4 w-4 mr-1" />
              Clear
            </Button>
          </div>
          <div className="flex items-center gap-2 mb-4" data-testid="metric-mode-controls">
            <div className="inline-flex rounded-md border border-input bg-background">
              <Button
                variant={viewMode === "list" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("list")}
                className="rounded-r-none"
                data-testid="button-metric-view-list"
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "cards" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("cards")}
                className="rounded-l-none"
                data-testid="button-metric-view-cards"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </div>
            <div className="ml-auto">
              <AnimalExportButton
                animals={columnFilteredAnimals}
                disabled={viewMode === "cards"}
              />
            </div>
          </div>
        </>
      )}

      {/* Gate E6.2: Tabs for Current Population vs All Animals (hidden in metric mode) */}
      {!metricId && (
      <div className="mb-4">
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="current" data-testid="tab-current-population">
              Current Population
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-all-animals">
              All Animals (Historical)
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      )}

      {/* Gate E6.5: Outcome segmented control (History tab only, simplified, hidden in metric mode) */}
      {!metricId && activeTab === "history" && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-sm text-muted-foreground">Show:</span>
          <div className="inline-flex rounded-md border border-input bg-background">
            <Button
              variant={outcomeFilter === "all" ? "default" : "ghost"}
              size="sm"
              onClick={() => setOutcomeFilter("all")}
              className="rounded-r-none border-r"
              data-testid="button-outcome-all"
            >
              All Animals
            </Button>
            <Button
              variant={outcomeFilter === "outcomed" ? "default" : "ghost"}
              size="sm"
              onClick={() => setOutcomeFilter("outcomed")}
              className="rounded-l-none"
              data-testid="button-outcome-outcomed"
            >
              Outcomed Only
            </Button>
          </div>
        </div>
      )}

      {/* Filters (hidden in metric mode) */}
      {!metricId && <div className="sticky top-0 z-40 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-3 pb-4 border-b bg-muted/80 backdrop-blur supports-[backdrop-filter]:bg-muted/60 mb-4">
      <Card>
        <CardContent className="p-5">
          <CollapsibleFilterHeader
            collapsed={filterCollapsed}
            onToggle={() => setFilterCollapsed((c) => !c)}
            description={description}
            hasActiveFilters={hasActiveFilters}
            clearLabel={isDefaultLocationActive ? "Clear default" : "Clear"}
            onClearFilters={() => {
              setSelectedLocations([]);
              setSelectedZone("all");
              setSelectedSpeciesList([]);
              setSelectedStatuses([]);
              setSelectedAdoptionLevels([]);
              setShowMedicalNotesOnly(false);
              setSearchQuery("");
              setOutcomeFilter("all");
              setSelectedEvalStatuses([]);
              setHoldFilter("all");
            }}
            descriptionTestId="text-animals-description"
          >
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, ID, or breed..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="input-search-animals"
                />
              </div>
            </div>

            {isLocationsLoading ? (
              <Skeleton className="h-9 w-[140px]" />
            ) : (
              <MultiSelectFilter
                label="Locations"
                options={activeLocations.map(loc => ({ value: loc.code, label: loc.label }))}
                selected={selectedLocations}
                onChange={(vals) => { setSelectedLocations(vals); setSelectedZone("all"); }}
                className="w-[140px]"
                data-testid="select-location-filter"
              />
            )}

            {/* Zone filter (only visible when exactly one location is selected) */}
            {selectedLocations.length === 1 && (
              <Select 
                value={selectedZone} 
                onValueChange={setSelectedZone}
                disabled={zonesForLocation.length === 0}
              >
                <SelectTrigger className="w-[160px]" data-testid="select-zone-filter">
                  <SelectValue placeholder="Kennel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Kennels</SelectItem>
                  {zonesForLocation.map((zone) => (
                    <SelectItem key={zone.id} value={zone.zoneCode} data-testid={`option-zone-${zone.zoneCode.replace(/\s+/g, '-').toLowerCase()}`}>
                      {resolveLabel(zone.zoneCode)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <MultiSelectFilter
              label="Species"
              options={[
                { value: "DOG", label: "Dogs" },
                { value: "CAT", label: "Cats" },
                { value: "OTHER", label: "Other" },
              ]}
              selected={selectedSpeciesList}
              onChange={setSelectedSpeciesList}
              className="w-[140px]"
              data-testid="select-species-filter"
            />

            <MultiSelectFilter
              label="Statuses"
              optionGroups={STATUS_TRACK_OPTION_GROUPS}
              selected={selectedStatuses}
              onChange={setSelectedStatuses}
              className="w-[150px]"
              data-testid="select-status-filter"
            />

            <MultiSelectFilter
              label="Adoption"
              options={[
                { value: "L1", label: "Level 1" },
                { value: "L2", label: "Level 2" },
                { value: "L3", label: "Level 3" },
                { value: "L4", label: "Level 4" },
                { value: "none", label: "No Level" },
              ]}
              selected={selectedAdoptionLevels}
              onChange={setSelectedAdoptionLevels}
              className="w-[150px]"
              data-testid="select-adoption-filter"
            />

            {/* Gate E6.4: Evaluation filter dropdown (both tabs) */}
            <MultiSelectFilter
              label="Evaluation"
              options={[
                { value: "needs_review", label: "Needs Review" },
                { value: "needs_recs", label: "Needs Recs" },
                { value: "complete", label: "Complete" },
                { value: "none", label: "No Evaluation" },
                { value: "other", label: "Other" },
              ]}
              selected={selectedEvalStatuses}
              onChange={setSelectedEvalStatuses}
              className="w-[160px]"
              data-testid="select-evaluation-filter"
            />

            {/* Gate I3.1: HOLD status filter */}
            <Select value={holdFilter} onValueChange={(v) => setHoldFilter(v as HoldFilter)}>
              <SelectTrigger className="w-[130px]" data-testid="select-hold-filter">
                <SelectValue placeholder="Hold Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Animals</SelectItem>
                <SelectItem value="onHold">On Hold Only</SelectItem>
                <SelectItem value="notOnHold">Not on Hold</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant={showMedicalNotesOnly ? "default" : "outline"}
              size="sm"
              onClick={() => setShowMedicalNotesOnly(!showMedicalNotesOnly)}
              className="gap-1"
              data-testid="button-medical-filter"
            >
              <Stethoscope className="h-4 w-4" />
              Medical DX
            </Button>

          </div>

          {/* Summary Stats - inside collapsible filter */}
          <div className="grid gap-4 grid-cols-2 md:grid-cols-5 mt-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>
              {activeTab === "current" ? "Filtered Animals" : "Filtered Animals"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold" data-testid="text-total-count">{filteredAnimals.length}</p>
            {activeTab === "history" && outcomedCount > 0 && (
              <p className="text-xs text-muted-foreground" data-testid="text-outcomed-count">
                ({outcomedCount} outcomed)
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Available</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-[hsl(var(--status-available))]">
              {filteredAnimals.filter(a => a.kennelStatusNorm === "AVAILABLE").length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Unavailable</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-[hsl(var(--status-unavailable))]">
              {filteredAnimals.filter(a => a.kennelStatusNorm === "UNAVAIL").length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1.5">
              Behavior
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>Animals flagged with behavior needs (kennel substatus "BEHAVIOR" or "BEH+MED").</p>
                </TooltipContent>
              </Tooltip>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-amber-500" data-testid="text-behavior-hold-count">
              {filteredAnimals.filter(a => a.substatusNorm === "BEHAVIOR" || a.substatusNorm === "BEH+MED").length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1.5">
              Medical
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>Animals flagged with medical needs (kennel substatus "MEDICAL" or "BEH+MED").</p>
                </TooltipContent>
              </Tooltip>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-purple-500" data-testid="text-medical-count">
              {filteredAnimals.filter(a => a.substatusNorm === "MEDICAL" || a.substatusNorm === "BEH+MED").length}
            </p>
          </CardContent>
        </Card>
          </div>
          </CollapsibleFilterHeader>
        </CardContent>
      </Card>

      {/* Gate F16: Results Controls - view toggle + sort, outside filter card */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 pt-3">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <span className="text-sm text-muted-foreground">View:</span>
            <div className="inline-flex rounded-md border border-input bg-background">
              <Button
                variant={viewMode === "list" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("list")}
                className="rounded-r-none"
                data-testid="button-view-list"
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "cards" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("cards")}
                className="rounded-l-none"
                data-testid="button-view-cards"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 flex-1 sm:flex-none min-w-0">
            <span className="text-sm font-medium text-muted-foreground shrink-0">Sort by</span>
            <Select
              value={`${sortColumn}:${sortDir}`}
              onValueChange={(v) => {
                const [col, dir] = v.split(":") as [SortColumn, SortDir];
                setSortColumn(col);
                setSortDir(dir);
              }}
            >
              <SelectTrigger className="w-full sm:w-[200px] min-w-0" data-testid="select-sort-by">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lastSeen:desc">Last Seen (newest)</SelectItem>
                <SelectItem value="lastSeen:asc">Last Seen (oldest)</SelectItem>
                <SelectItem value="intake:asc">Intake date (oldest first)</SelectItem>
                <SelectItem value="intake:desc">Intake date (newest first)</SelectItem>
                <SelectItem value="name:asc">Name (A-Z)</SelectItem>
                <SelectItem value="name:desc">Name (Z-A)</SelectItem>
                <SelectItem value="species:asc">Species</SelectItem>
                <SelectItem value="status:asc">Status</SelectItem>
                <SelectItem value="location:asc">Location</SelectItem>
                <SelectItem value="zone:asc">Kennel</SelectItem>
                <SelectItem value="adoptionLevel:asc">Adoption Level</SelectItem>
                <SelectItem value="evaluationStatus:asc">Evaluation Status</SelectItem>
                <SelectItem value="medical:asc">Medical (notes first)</SelectItem>
                <SelectItem value="signals:asc">Signals (flagged first)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="ml-auto">
            <AnimalExportButton
              animals={columnFilteredAnimals}
              disabled={viewMode === "cards"}
            />
          </div>
        </div>
      </div>}

      {columnFilters.hasAnyActive() && viewMode === "list" && (
        <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid="text-column-filter-info">
          <Info className="h-3 w-3 shrink-0" />
          Column filters active — counts above do not reflect column filters.
        </p>
      )}

      {/* Gate E6.6: Conditional rendering based on view mode */}
      {viewMode === "cards" ? (
        <div className={`grid gap-4 md:grid-cols-2 lg:grid-cols-3 transition-opacity duration-150 ${isFetching && !isLoading ? "opacity-60" : "opacity-100"}`}>
          {filteredAnimals.map((animal) => (
            <AnimalCard 
              key={animal.id} 
              animal={animal} 
              showOutcomedBadge={activeTab === "history"}
              onViewPreview={handleViewPreview}
              onAddTo={handleAddTo}
            />
          ))}
        </div>
      ) : (
        <div className={`transition-opacity duration-150 ${isFetching && !isLoading ? "opacity-60" : "opacity-100"}`}>
        <AnimalListView
          animals={columnFilteredAnimals}
          showOutcomedBadge={activeTab === "history"}
          showLocation={showLocation}
          onAnimalClick={handleAnimalClick}
          onViewPreview={handleViewPreview}
          onAddTo={handleAddTo}
          columnFilterValues={columnFilterValues}
          columnFilterSelections={columnFilters.filters}
          columnFilterIsActive={columnFilters.isActive}
          onColumnFilterChange={columnFilters.setFilter}
          onColumnFilterClear={columnFilters.clearFilter}
          hasAnyColumnFilter={columnFilters.hasAnyActive()}
          onClearAllColumnFilters={columnFilters.clearAll}
          totalBeforeColumnFilter={filteredAnimals.length}
          sortColumn={sortColumn}
          sortDir={sortDir}
          onColumnSort={handleColumnSort}
        />
        </div>
      )}

      {/* Gate I3.1: Empty state rules - use SearchEmptyState when filters active, onboarding otherwise */}
      {filteredAnimals.length === 0 && (
        <Card>
          <CardContent className="py-8">
            {hasActiveFilters ? (
              <SearchEmptyState />
            ) : (
              <div className="text-center space-y-2">
                <Dog className="h-12 w-12 mx-auto text-muted-foreground" />
                <h3 className="text-lg font-medium">No animals yet</h3>
                <p className="text-sm text-muted-foreground">
                  Upload your first All Pets report to get started.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {drawerAnimalId && (
        <AnimalDetailDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          animalId={drawerAnimalId}
          context="animals"
        />
      )}

      {linkerAnimalId && (
        <EntityLinkerDialog
          open={linkerOpen}
          onOpenChange={setLinkerOpen}
          entityType="animal"
          entityId={linkerAnimalId}
          entityLabel={linkerAnimalName ? `${linkerAnimalName} (${linkerAnimalId})` : linkerAnimalId}
          animalSpecies={linkerAnimalSpecies}
          animalPhotoUrl={linkerAnimalPhotoUrl}
        />
      )}
    </div>
  );
}
